import * as Dialog from '@radix-ui/react-dialog';
import { ampli } from '@/ampli';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useWalletClient } from 'wagmi';

import { normalizeChainIdFromZeroXApi } from '@/lib/api/0x-cross-chain/constants';
import { tokenFormatter } from '@/lib/formatting';

import { getBridgeDisplayName } from '@/components/TransactionModule/CrossChainMode/bridgeProviders';

import { ADVANCED_ORDER_TYPE_LABELS, type AdvancedOrderType } from '@/constants/advancedOrder';
import {
  CHAIN_EXPLORER_NAMES,
  NETWORK_NAME_PER_CHAIN_ID,
  SUPPORTED_CHAINS_BY_CHAIN_ID,
} from '@/constants/chain';

import { useCrossChainStatus } from '@/hooks/0xCrossChain/useStatus';
import { useTokenResult } from '@/hooks/tokenQueries/useTokenResult';
import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';

import { transactionsSelector, useTransactionStore } from '@/store/transaction';
import { CrossChainTransaction } from '@/store/transaction/types';
import { getTxHash } from '@/store/transaction/utils';

import { buttons } from '@/styles/buttons';
import { linkStyles } from '@/styles/links';
import { fontStyles } from '@/styles/typography';

import { BridgeAvatar } from '@/ui/Avatars/BridgeAvatar';
import { ChainAvatar } from '@/ui/Avatars/ChainAvatar';
import { TokenAvatar } from '@/ui/Avatars/TokenAvatar';
import { ArrowExternalIcon } from '@/ui/Icons/ArrowExternalIcon';
import { XIcon } from '@/ui/Icons/XIcon';
import { ExternalLink } from '@/ui/Link/ExternalLink';
import { Modal } from '@/ui/Modal';

import { Route } from '@/utils/0x.types';
import {
  FAILURE_REASON_LABELS,
  getTradeStatus,
  getTradeType,
  REFUND_STATUS_LABELS,
  TRADE_TYPE,
} from '@/utils/0x/history/status';
import type {
  CrossChainFailureReason,
  CrossChainRefundStatus,
  ProtocolVersion,
} from '@/utils/0x/history/types';
// utils
import { getBlockExplorerTransactionURL } from '@/utils/blockExplorers';
import { GaslessStatusResponse } from '@/utils/gasless/types';
import { MATCHA_LIMIT_ORDER } from '@/utils/liquidity-sources';
import { ChainAddress, ChainHash, EthereumAddress } from '@/utils/models';

export interface TradeDetails {
  chainId: number;
  destinationChainId?: number;
  makerToken: ChainAddress;
  takerToken: ChainAddress;
  makerAmount: number;
  takerAmount: number;
  makerTokenSymbol?: string;
  takerTokenSymbol?: string;
  status: string;
  timestamp: string;
  expiry?: number;
  liquiditySource?: string;
  advancedOrderType?: AdvancedOrderType;
  transactionHash?: ChainHash;
  tradeId: number;
  usdValue?: number;
  protocolVersion: ProtocolVersion;
  /** Recipient address for swap-and-send transactions (undefined means sent to self) */
  recipient?: string;

  // --- Cross-chain failure / refund fields ---
  /** Why the bridge failed. */
  failureReason?: CrossChainFailureReason;
  /** Status of the recovery/refund step after a bridge failure. */
  refundStatus?: CrossChainRefundStatus;
  /** Transaction hash of the refund. */
  refundTxHash?: string;
  /** Chain ID where the refund occurred. */
  refundChainId?: number;
  /** Token symbol that was refunded. */
  refundTokenSymbol?: string;
  /** Amount refunded (human-readable). */
  refundAmount?: number;
  /** USD value of the refund. */
  refundUsd?: number;
}

interface TradeDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange?: Dispatch<SetStateAction<boolean>>;
  trade: TradeDetails;
  route?: Route;
  handleShowSankeyDiagram: () => void;
}

export const enum TRADE_STATUS {
  PENDING = 'pending',
  SUCCESS = 'success',
  REVERTED = 'reverted',
  CANCELLED_SINGLE = 'cancelledSingle',
  CANCELLED_MULTI = 'cancelledMulti',
}

/**
 * Show details about a pending or completed trade
 * @returns JSX.Element
 */
export const TradeDetailsModal = ({
  isOpen,
  onClose,
  onOpenChange,
  trade,
  route,
  handleShowSankeyDiagram,
}: TradeDetailsModalProps) => {
  const { formatFiat } = useFiatDisplay();
  // trade info
  const {
    chainId,
    destinationChainId,
    makerToken,
    makerAmount,
    takerToken,
    takerAmount,
    timestamp,
    expiry,
    liquiditySource,
    usdValue,
    protocolVersion,
    status,
  } = trade;
  const tradeType = getTradeType({
    destinationChainId,
    liquiditySource,
    recipient: trade.recipient,
  });
  const isCrossChainTrade = tradeType === TRADE_TYPE.CROSS_CHAIN;
  let { transactionHash } = trade;
  const isLimitOrder = liquiditySource && liquiditySource === MATCHA_LIMIT_ORDER;
  const typeLabel = trade.advancedOrderType
    ? ADVANCED_ORDER_TYPE_LABELS[trade.advancedOrderType]
    : tradeType;
  const tradeInfo = {
    sellTokenAddress: isLimitOrder ? makerToken : takerToken,
    sellTokenAmount: isLimitOrder ? makerAmount : takerAmount,
    buyTokenAddress: isLimitOrder ? takerToken : makerToken,
    buyTokenAmount: isLimitOrder ? takerAmount : makerAmount,
  };
  const { ethereumWallet } = useMatchaWallets();
  const allTransactions = useTransactionStore(transactionsSelector);
  const transaction =
    ethereumWallet?.address && transactionHash
      ? allTransactions[ethereumWallet.address as EthereumAddress]?.[chainId]?.find(
          (tx) => transactionHash === tx.hash,
        )
      : undefined;
  if (transaction && transaction.transactionType === 'gasless') {
    transactionHash = (transaction.raw as GaslessStatusResponse)?.transactions?.[0]?.hash;
  }
  if (transaction && transaction.transactionType === 'intents') {
    transactionHash = getTxHash(transaction);
  }
  const { token: sellToken } = useTokenResult(tradeInfo.sellTokenAddress, chainId);
  const { token: buyToken } = useTokenResult(
    tradeInfo.buyTokenAddress,
    destinationChainId ?? chainId,
  );

  // Check if this is a cross-chain transaction
  const isCrossChain = transaction?.transactionType === 'cross-chain';
  const crossChainTx = isCrossChain ? (transaction as CrossChainTransaction) : undefined;

  // Fetch 0x cross-chain status if needed
  const { data: crossChainStatus } = useCrossChainStatus(
    isCrossChain && crossChainTx?.quoteId && transactionHash
      ? {
          originChain: chainId,
          originTxHash: transactionHash,
          quoteId: crossChainTx.quoteId,
        }
      : undefined,
  );
  const effectiveFailureReason =
    trade.failureReason ??
    crossChainStatus?.failure?.reason ??
    crossChainTx?.failureContext?.reason;
  const effectiveRefundStatus =
    trade.refundStatus ?? crossChainStatus?.failure?.status ?? crossChainTx?.failureContext?.status;

  // Get destination tx info from cached transaction or fresh status
  const statusDestinationTxHash =
    crossChainTx?.destinationTxHash ||
    crossChainStatus?.transactions?.find((t) => Number(t.chainId) !== chainId)?.txHash;
  const statusDestinationChainId =
    crossChainTx?.destinationChainId ||
    (crossChainStatus?.transactions?.find((t) => Number(t.chainId) !== chainId)?.chainId
      ? normalizeChainIdFromZeroXApi(
          crossChainStatus!.transactions.find((t) => Number(t.chainId) !== chainId)!.chainId,
        )
      : undefined);

  // --- Recovery flow for manual_action_required trades ---
  // Only poll /status when the modal is open AND the trade needs manual recovery.
  const needsRecovery = isOpen && effectiveRefundStatus === 'manual_action_required';
  const { data: recoveryStatus } = useCrossChainStatus(
    needsRecovery && transactionHash
      ? { originChain: chainId, originTxHash: transactionHash }
      : undefined,
    { refetchInterval: 5_000 },
  );
  const manualTx = recoveryStatus?.failure?.recovery?.manualTransaction;
  const { data: walletClient } = useWalletClient();

  const [recoveryTxHash, setRecoveryTxHash] = useState<string>();
  const [isRecoveryLoading, setIsRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string>();

  const handleRecoverFunds = useCallback(async () => {
    if (!manualTx || !walletClient) return;
    setIsRecoveryLoading(true);
    setRecoveryError(undefined);
    setRecoveryTxHash(undefined);
    try {
      const txHash = await walletClient.sendTransaction({
        account: walletClient.account,
        chain: SUPPORTED_CHAINS_BY_CHAIN_ID[manualTx.chainId],
        to: manualTx.to as EthereumAddress,
        data: manualTx.data as `0x${string}`,
        value: BigInt(manualTx.value),
      });
      setRecoveryTxHash(txHash);
    } catch (err: unknown) {
      setRecoveryError(err instanceof Error ? err.message : 'Transaction failed');
    } finally {
      setIsRecoveryLoading(false);
    }
  }, [manualTx, walletClient]);

  const makerRateAmount = tradeInfo.buyTokenAmount / tradeInfo.sellTokenAmount;

  const onClickSankey = () => {
    ampli.sankeyChartExpanded({
      ui_location: 'trade_history',
    });
    handleShowSankeyDiagram();
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} onOpenChange={onOpenChange}>
      <div className="items-center flex justify-between px-6 py-3">
        <Dialog.Title className={fontStyles({ variant: 'H5' })}>Trade details</Dialog.Title>
        <Dialog.Close
          className={buttons({
            color: 'transparent',
            shape: 'rounded',
            size: 'small',
            iconOnly: true,
          })}
        >
          <XIcon aria-hidden className="block h-4 w-4" />
        </Dialog.Close>
      </div>

      <div className="pb-6 px-6">
        <VisuallyHidden>
          <Dialog.Description>
            More details about your trade, including the exact amounts and USD value.
          </Dialog.Description>
        </VisuallyHidden>

        <ul>
          <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              Sell
            </span>
            <span
              className={fontStyles({ variant: 'BODY_MD', class: 'flex items-center capitalize' })}
            >
              <TokenAvatar
                address={sellToken?.address}
                chainId={sellToken?.chainId}
                alt={`${sellToken?.name} logo`}
                size={16}
                showChainLogo={false}
              />
              <span className="ml-2">
                {tokenFormatter.format(tradeInfo.sellTokenAmount, { symbol: sellToken?.symbol })}
                {!!destinationChainId && (
                  <span className="text-token-gray-800 normal-case">
                    {' '}
                    on {NETWORK_NAME_PER_CHAIN_ID[chainId]}
                  </span>
                )}
              </span>
            </span>
          </li>

          <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              Buy
            </span>
            <span
              className={fontStyles({ variant: 'BODY_MD', class: 'flex items-center capitalize' })}
            >
              <TokenAvatar
                address={buyToken?.address}
                chainId={buyToken?.chainId}
                alt={`${buyToken?.name} logo`}
                size={16}
                showChainLogo={false}
              />
              <span className="ml-2">
                {tokenFormatter.format(tradeInfo.buyTokenAmount, { symbol: buyToken?.symbol })}
                {!!destinationChainId && (
                  <span className="text-token-gray-800 normal-case">
                    {' '}
                    on {NETWORK_NAME_PER_CHAIN_ID[destinationChainId]}
                  </span>
                )}
              </span>
            </span>
          </li>

          <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              Network
            </span>
            <span
              className={fontStyles({ variant: 'BODY_MD', class: 'flex items-center capitalize' })}
            >
              <ChainAvatar
                chainId={chainId}
                alt={`${NETWORK_NAME_PER_CHAIN_ID[chainId]} logo`}
                size={16}
                className="mr-1 ml-2"
              />
              {NETWORK_NAME_PER_CHAIN_ID[chainId]}
              {!!destinationChainId && (
                <>
                  →
                  <ChainAvatar
                    chainId={destinationChainId}
                    alt={`${NETWORK_NAME_PER_CHAIN_ID[destinationChainId]} logo`}
                    size={16}
                    className="mr-1 ml-2"
                  />
                  {NETWORK_NAME_PER_CHAIN_ID[destinationChainId]}
                </>
              )}
            </span>
          </li>

          {/* Bridge provider for cross-chain transactions */}
          {(() => {
            const bridgeProvider =
              crossChainTx?.tradeStatus?.bridge ||
              crossChainStatus?.bridge ||
              (destinationChainId ? trade.liquiditySource : undefined);
            if (!bridgeProvider) return null;
            return (
              <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
                <span
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: 'text-token-gray-800 flex items-center',
                  })}
                >
                  Provider
                </span>
                <span
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: 'flex items-center gap-1.5',
                  })}
                >
                  <BridgeAvatar name={bridgeProvider} size={16} />
                  {getBridgeDisplayName(bridgeProvider)}
                </span>
              </li>
            );
          })()}

          {protocolVersion === 'Settler' && !isCrossChainTrade && (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                Route
              </span>
              {route?.fills.length && (
                <button
                  className={linkStyles({
                    color: 'purple',
                    font: 'BODY_MD',
                    underline: true,
                    class: 'flex items-center capitalize',
                  })}
                  onClick={onClickSankey}
                >
                  {route?.fills.length &&
                    `${route.fills.length} ${route.fills.length > 1 ? 'sources' : 'source'}`}
                </button>
              )}
            </li>
          )}

          <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              Rate
            </span>
            {sellToken && buyToken && (
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center capitalize',
                })}
              >
                1 {sellToken.symbol} ={' '}
                {tokenFormatter.format(makerRateAmount, { symbol: buyToken.symbol })}
              </span>
            )}
          </li>

          <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              Date
            </span>
            <span
              className={fontStyles({ variant: 'BODY_MD', class: 'flex items-center capitalize' })}
            >
              {new Date(timestamp).toLocaleString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </span>
          </li>

          {expiry && (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                Expires
              </span>
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center capitalize',
                })}
              >
                {new Date(expiry).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                })}
              </span>
            </li>
          )}

          <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              Status
            </span>
            <span
              className={fontStyles({ variant: 'BODY_MD', class: 'flex items-center capitalize' })}
            >
              {getTradeStatus({
                status,
                liquiditySource,
                refundStatus: effectiveRefundStatus,
                failureReason: effectiveFailureReason,
              })}
            </span>
          </li>

          {/* --- Cross-chain failure / refund details --- */}

          {effectiveFailureReason && (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                Failure Reason
              </span>
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center capitalize',
                })}
              >
                {FAILURE_REASON_LABELS[effectiveFailureReason] ?? effectiveFailureReason}
              </span>
            </li>
          )}

          {effectiveRefundStatus && (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                Recovery
              </span>
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center capitalize',
                })}
              >
                {REFUND_STATUS_LABELS[effectiveRefundStatus] ?? effectiveRefundStatus}
              </span>
            </li>
          )}

          {trade.refundAmount != null && trade.refundTokenSymbol && (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                Refund Amount
              </span>
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center',
                })}
              >
                {tokenFormatter.format(trade.refundAmount, { symbol: trade.refundTokenSymbol })}
                {trade.refundUsd != null && (
                  <span className="text-token-gray-800 ml-1">({formatFiat(trade.refundUsd)})</span>
                )}
              </span>
            </li>
          )}

          {effectiveRefundStatus === 'manual_action_required' && (
            <li className="border-token-gray-300 border-t flex flex-col gap-2 min-h-12 py-3">
              <span
                className={fontStyles({
                  variant: 'BODY_SM',
                  class: 'text-token-gray-800',
                })}
              >
                In some cases (e.g. Stargate V2), funds may have arrived on the destination chain
                but were not delivered to your wallet. Submitting a recovery transaction will
                re-trigger delivery.
              </span>

              {manualTx && !recoveryTxHash && (
                <button
                  type="button"
                  disabled={isRecoveryLoading || !walletClient}
                  onClick={handleRecoverFunds}
                  className={buttons({
                    size: 'small',
                    shape: 'rounded',
                    color: 'purple',
                    class: 'w-full',
                  })}
                >
                  {isRecoveryLoading ? 'Waiting for wallet...' : 'Recover Funds'}
                </button>
              )}

              {!manualTx && !recoveryStatus && needsRecovery && (
                <span
                  className={fontStyles({
                    variant: 'CAPTION',
                    class: 'text-token-gray-700',
                  })}
                >
                  Checking for recovery transaction...
                </span>
              )}

              {!manualTx && recoveryStatus && (
                <span
                  className={fontStyles({
                    variant: 'CAPTION',
                    class: 'text-token-gray-700',
                  })}
                >
                  No recovery transaction available. Please contact support for assistance.
                </span>
              )}

              {recoveryTxHash && manualTx && (
                <span
                  className={fontStyles({
                    variant: 'BODY_SM',
                    class: 'text-token-green-1200 flex items-center gap-1',
                  })}
                >
                  Recovery submitted!{' '}
                  <ExternalLink
                    className={linkStyles({
                      color: 'purple',
                      font: 'BODY_SM',
                      underline: true,
                      class: 'inline-flex items-center',
                    })}
                    href={getBlockExplorerTransactionURL(manualTx.chainId, recoveryTxHash)}
                  >
                    View transaction <ArrowExternalIcon />
                  </ExternalLink>
                </span>
              )}

              {recoveryError && (
                <span
                  className={fontStyles({
                    variant: 'CAPTION',
                    class: 'text-token-red-1000',
                  })}
                >
                  {recoveryError}
                </span>
              )}
            </li>
          )}

          {trade.recipient && (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                Recipient
              </span>
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center',
                })}
              >
                {trade.recipient.slice(0, 6)}…{trade.recipient.slice(-4)}
              </span>
            </li>
          )}

          {usdValue && (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                USD Value
              </span>
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'flex items-center capitalize',
                })}
              >
                {formatFiat(usdValue)}
              </span>
            </li>
          )}

          <li className="border-token-gray-300 border-t items-center flex justify-between h-12 last:border-b last:border-b-token-gray-300">
            <span
              className={fontStyles({
                variant: 'BODY_MD',
                class: 'text-token-gray-800 flex items-center',
              })}
            >
              Type
            </span>
            <span
              className={fontStyles({ variant: 'BODY_MD', class: 'flex items-center capitalize' })}
            >
              {typeLabel}
            </span>
          </li>

          {/* For 0x cross-chain (store-sourced or CSV-sourced), show Source + Destination/Refund explorers */}
          {transactionHash && (isCrossChain || trade.failureReason) ? (
            <>
              <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
                <span
                  className={fontStyles({
                    variant: 'BODY_MD',
                    class: 'text-token-gray-800 flex items-center',
                  })}
                >
                  Source Explorer
                </span>
                <span
                  className={linkStyles({
                    color: 'purple',
                    font: 'BODY_MD',
                    underline: true,
                    class: 'flex items-center capitalize',
                  })}
                >
                  <ExternalLink
                    className="flex items-center"
                    href={`${getBlockExplorerTransactionURL(chainId, transactionHash)}`}
                  >
                    {CHAIN_EXPLORER_NAMES[chainId] || 'Blockchain Explorer'}
                    &nbsp;
                    <ArrowExternalIcon />
                  </ExternalLink>
                </span>
              </li>

              {statusDestinationTxHash && statusDestinationChainId && (
                <li className="border-token-gray-300 border-t items-center flex justify-between h-12">
                  <span
                    className={fontStyles({
                      variant: 'BODY_MD',
                      class: 'text-token-gray-800 flex items-center',
                    })}
                  >
                    Destination Explorer
                  </span>
                  <span
                    className={linkStyles({
                      color: 'purple',
                      font: 'BODY_MD',
                      underline: true,
                      class: 'flex items-center capitalize',
                    })}
                  >
                    <ExternalLink
                      className="flex items-center"
                      href={`${getBlockExplorerTransactionURL(statusDestinationChainId, statusDestinationTxHash)}`}
                    >
                      {CHAIN_EXPLORER_NAMES[statusDestinationChainId] || 'Blockchain Explorer'}
                      &nbsp;
                      <ArrowExternalIcon />
                    </ExternalLink>
                  </span>
                </li>
              )}

              {trade.refundTxHash && trade.refundChainId && (
                <li className="border-token-gray-300 border-t items-center flex justify-between h-12 border-b border-b-token-gray-300">
                  <span
                    className={fontStyles({
                      variant: 'BODY_MD',
                      class: 'text-token-gray-800 flex items-center',
                    })}
                  >
                    Refund Explorer
                  </span>
                  <span
                    className={linkStyles({
                      color: 'purple',
                      font: 'BODY_MD',
                      underline: true,
                      class: 'flex items-center capitalize',
                    })}
                  >
                    <ExternalLink
                      className="flex items-center"
                      href={`${getBlockExplorerTransactionURL(trade.refundChainId, trade.refundTxHash)}`}
                    >
                      {CHAIN_EXPLORER_NAMES[trade.refundChainId] || 'Blockchain Explorer'}
                      &nbsp;
                      <ArrowExternalIcon />
                    </ExternalLink>
                  </span>
                </li>
              )}
            </>
          ) : transactionHash ? (
            <li className="border-token-gray-300 border-t items-center flex justify-between h-12 border-b border-b-token-gray-300">
              <span
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: 'text-token-gray-800 flex items-center',
                })}
              >
                Block Explorer
              </span>
              <span
                className={linkStyles({
                  color: 'purple',
                  font: 'BODY_MD',
                  underline: true,
                  class: 'flex items-center capitalize',
                })}
              >
                {!!destinationChainId ? (
                  <ExternalLink
                    href={`https://socketscan.io/tx/${transactionHash}`}
                    className="flex items-center"
                  >
                    Socket &nbsp;
                    <ArrowExternalIcon />
                  </ExternalLink>
                ) : (
                  <ExternalLink
                    className="flex items-center"
                    href={`${getBlockExplorerTransactionURL(chainId, transactionHash)}`}
                  >
                    {CHAIN_EXPLORER_NAMES[chainId] || 'Blockchain Explorer'}
                    &nbsp;
                    <ArrowExternalIcon />
                  </ExternalLink>
                )}
              </span>
            </li>
          ) : null}
        </ul>
      </div>
    </Modal>
  );
};
